export type OfferActivationStatus = 'active' | 'redeemed' | 'expired' | 'revoked' | string;
export type OfferAudienceType =
  | 'all'
  | 'by_level'
  | 'by_xp'
  | 'by_local_xp'
  | 'by_visits'
  | 'regular_guests';
export type OfferState = 'available' | 'soon' | 'locked';
export type OfferCategory = 'all' | OfferState;
export type OfferRequirementType = 'none' | 'level' | 'xp' | 'local_xp' | 'visits';

export type OfferRequirement = {
  type: OfferRequirementType;
  required: number | null;
  current: number | null;
  percent: number | null;
};

export type GuestOffer = {
  id: string;
  title: string;
  description: string;
  starts_at: string | null;
  ends_at: string | null;
  total_limit: number | null;
  activated_count: number;
  audience_type: OfferAudienceType;
  state: OfferState;
  requirement: OfferRequirement;
  missing: {
    type: OfferRequirementType;
    amount: number | null;
  } | null;
  venue: {
    id: string;
    display_name: string | null;
    address: string | null;
    avatar_url: string | null;
  };
  my_activation: {
    id: string;
    status: OfferActivationStatus;
  } | null;
};

export type GuestActivation = {
  id: string;
  status: OfferActivationStatus;
  activated_at: string | null;
  expires_at: string | null;
  redeemed_at: string | null;
  offer: {
    id: string;
    title: string;
    description: string;
  };
  venue: {
    id: string;
    display_name: string | null;
    address: string | null;
  };
};

export type GuestOfferPageMeta = {
  counts: Record<OfferCategory, number>;
  category: OfferCategory;
  current_page: number;
  per_page: number;
  total: number;
  has_more: boolean;
};

export type GuestOfferPage = {
  data: GuestOffer[];
  meta: GuestOfferPageMeta;
};

export type OfferContext = {
  mode: 'city' | 'venue';
  city: string;
  venueId?: string;
};

type ApiErrorPayload = {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string[]>;
  };
};

export type DataEnvelope<T> = { data: T };
export type CollectionEnvelope<T> = { data: T[] };

export class GuestOffersApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly payload: ApiErrorPayload | null;

  constructor(status: number, payload: ApiErrorPayload | null) {
    super(payload?.error?.message ?? `API request failed with status ${status}`);
    this.name = 'GuestOffersApiError';
    this.status = status;
    this.code = payload?.error?.code ?? null;
    this.payload = payload;
  }
}

export function offerStateLabel(offer: GuestOffer): string {
  if (offer.my_activation?.status === 'redeemed') return 'Получено';
  if (offer.my_activation?.status === 'active') return 'Активировано';
  if (offer.my_activation?.status === 'expired' || offer.my_activation?.status === 'revoked') return 'Недоступно';
  if (offer.state === 'available') return 'Доступно';
  if (offer.state === 'soon') return 'Скоро';
  return 'Недоступно';
}

export function offerRequirementText(offer: GuestOffer): string | null {
  const { type, current, required } = offer.requirement;
  if (type === 'none' || required === null) return null;

  if (type === 'local_xp') {
    return current === null
      ? `Нужно ${required.toLocaleString('ru-RU')} XP в этом заведении`
      : `${current.toLocaleString('ru-RU')} / ${required.toLocaleString('ru-RU')} XP в этом заведении`;
  }

  if (type === 'xp') {
    return current === null
      ? `Нужно ${required.toLocaleString('ru-RU')} XP Хоппера`
      : `${current.toLocaleString('ru-RU')} / ${required.toLocaleString('ru-RU')} XP Хоппера`;
  }

  if (type === 'level') {
    return current === null
      ? `Нужен уровень Хоппера ${required}`
      : `Уровень ${current} из требуемого ${required}`;
  }

  if (type === 'visits') {
    return current === null
      ? `Нужно ${required} подтверждённых визитов`
      : `${current} из ${required} подтверждённых визитов`;
  }

  return null;
}

export function offerMissingText(offer: GuestOffer): string | null {
  const missing = offer.missing;
  if (!missing || missing.amount === null || missing.amount <= 0) return null;

  if (missing.type === 'xp') return `Осталось ${missing.amount.toLocaleString('ru-RU')} XP Хоппера`;
  if (missing.type === 'local_xp') return `Осталось ${missing.amount.toLocaleString('ru-RU')} XP в заведении`;
  if (missing.type === 'visits') return `Осталось визитов: ${missing.amount}`;
  if (missing.type === 'level') return `Осталось уровней: ${missing.amount}`;
  return null;
}

let csrfToken: string | null = null;

async function parseJson(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';

  if (!contentType.includes('application/json')) {
    return null;
  }

  return response.json();
}

async function ensureCsrf(): Promise<string> {
  if (csrfToken) {
    return csrfToken;
  }

  const response = await fetch('/api/v1/auth/csrf', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  const body = (await parseJson(response)) as { csrf_token?: string } | null;

  if (!response.ok || !body?.csrf_token) {
    throw new GuestOffersApiError(response.status, body as ApiErrorPayload | null);
  }

  csrfToken = body.csrf_token;
  return csrfToken;
}

export async function guestOffersFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);

  headers.set('Accept', 'application/json');

  if (options.body !== undefined && options.body !== null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (method !== 'GET' && method !== 'HEAD') {
    headers.set('X-CSRF-TOKEN', await ensureCsrf());
  }

  const response = await fetch(url, {
    ...options,
    method,
    credentials: 'same-origin',
    headers,
  });
  const body = await parseJson(response);

  if (!response.ok) {
    throw new GuestOffersApiError(response.status, body as ApiErrorPayload | null);
  }

  return body as T;
}

export function offersEndpoint(context: OfferContext): string {
  if (context.mode === 'venue' && context.venueId) {
    return `/api/v1/venues/${encodeURIComponent(context.venueId)}/offers`;
  }

  return `/api/v1/cities/${encodeURIComponent(context.city)}/offers`;
}

export function offersPageEndpoint(context: OfferContext, category: OfferCategory, page: number): string {
  const params = new URLSearchParams({ category, page: String(page) });
  return `${offersEndpoint(context)}?${params.toString()}`;
}
